import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { Checkbox, FormControl, FormHelperText, useTheme } from '@mui/material';
import strings from 'common/Translation/Translate';
import { FC, useState } from 'react';
import Select, { StylesConfig, components } from 'react-select';
import { validateInput } from '../../utils/validators/Validators';
import { FormValidators } from '../Form';
import Paragraph from '../Typography/Typography';
import './styles.scss';

const Option = (props: any) => {
  return (
    <components.Option {...props}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          width: '100%',
          minHeight: '24px',
        }}>
        <Checkbox
          checked={props.isSelected}
          onChange={() => null}
          style={{
            margin: 0,
            padding: 0,
            flexShrink: 0,
          }}
        />
        <label
          title={props.label}
          style={{
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            marginLeft: '12px',
            display: 'block',
            flex: 1,
            cursor: 'default',
            lineHeight: '24px',
            margin: 0,
            padding: 0,
          }}>
          {props.label}
        </label>
      </div>
    </components.Option>
  );
};

interface OwnProps {
  field?: string;
  disabled?: boolean;
  placeholder?: string;
  autoFocus?: boolean;
  typeValue?: string;
  onChange: (value: string, field: string, error?: { error: boolean; message: string }) => void;
  validators?: FormValidators[];
  hasError?: boolean;
  fieldError?: string | boolean;
  isSearchable?: boolean;
  label?: string;
  disableFuture?: boolean;
  labelId?: string;
  inputValue: any;
  options: any[];
  inputProps?: any;
  className?: string;
  style?: any;
  isMulti?: boolean;
  isClearable?: boolean;
  id: any;
}
const MultiSelect: FC<OwnProps> = ({
  hasError,
  validators,
  onChange,
  field,
  inputValue,
  options,
  fieldError,
  disabled,
  className,
  label,
  id,
  style,
  isSearchable = true,
  isMulti,
}) => {
  const theme = useTheme();
  const [error, setError] = useState<{
    error: boolean;
    message: string;
  } | null>(null);

  const calculateMenuWidth = () => {
    if (!options || options.length === 0) return 250;

    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    if (!context) return 250;

    context.font = '14px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';

    let maxWidth = 0;
    options.forEach((option: any) => {
      const textWidth = context.measureText(option.label || '').width;
      maxWidth = Math.max(maxWidth, textWidth);
    });

    const totalWidth = maxWidth + 80; // 80px for checkbox, margins, and buffer

    return Math.max(200, Math.min(400, totalWidth));
  };
  const handleChange = (selectedOption: any) => {
    const inputError = validateInput(validators as FormValidators[], selectedOption);
    setError(inputError);
    onChange(selectedOption, field as string, inputError);
  };
  const arrowDownIcon = (props: any) => {
    return (
      <components.DropdownIndicator {...props}>
        <ArrowDropDownIcon />
      </components.DropdownIndicator>
    );
  };
  const colourStyles: StylesConfig<any> = {
    valueContainer: base => ({
      ...base,
      maxHeight: '37px',
      overflowY: 'auto',
    }),
    control: styles => ({ ...styles, backgroundColor: 'white' }),
    option: (styles, { data, isDisabled, isFocused, isSelected }) => {
      return {
        ...styles,
        backgroundColor: 'white',
        color: 'black',
        cursor: isDisabled ? 'not-allowed' : 'default',
        padding: '6px 12px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-start',
        minWidth: `${calculateMenuWidth()}px`,
        minHeight: '36px',
        ':hover': {
          ...styles[':hover'],
          backgroundColor: 'lightgrey',
        },
      };
    },
  };

  const customMessage = () => (!error && hasError && fieldError ? strings.requiredField : '');

  return (
    <>
      {label ? <Paragraph sx={{ fontSize: '0.75rem', marginBottom: '6px' }}>{label}</Paragraph> : null}
      <FormControl
        sx={{
          '.select': {
            fontFamily: theme.typography.fontFamily,
            fontSize: theme.typography.fontSize,
          },
          "span[class$='indicatorSeparator']": {
            display: 'none',
          },
          '&.mainError': {
            "div[class$='control']": {
              borderColor: '#d32f2f',
            },
            "div[class$='placeholder']": {
              color: '#d32f2f',
            },
          },
          "div[class$='listbox'] > input:active": {
            color: 'white',
          },
          "div[class$='control']": {
            color: !disabled ? theme.palette.primary.main : 'gray',
            borderColor: !disabled ? theme.palette.secondary.main : 'lightgray',
            minHeight: '1.4375em',
            backgroundColor: !disabled ? 'white' : '#d8d8d8',
            borderRadius: '0%',
            height: '39px',
          },
          "div[class$='multiValue']": {
            backgroundColor: !disabled ? theme.palette.secondary.dark : 'white',
            paddingRight: !disabled ? 0 : 1,
          },
          "div[class$='MultiValueGeneric']": {
            fontSize: '12px !important',
          },
          "div[class$='MultiValueRemove']": {
            display: !disabled ? 'flex' : 'none',
          },
          '.MuiSvgIcon-root': {
            color: theme.palette.primary.main,
            visibility: !disabled ? 'visible' : 'hidden',
          },
          "div[class$='menu']": {
            zIndex: 9999,
            minWidth: `${calculateMenuWidth()}px`,
          },
          "div[class$='option']": {
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          },

          ...style,
        }}
        error={!!((error && error.error) || (!error && hasError && fieldError))}
        className={`dropdown ${className} ${
          (error && error.error) || (!error && hasError && fieldError) ? 'mainError' : ''
        }`}
        fullWidth>
        <Select
          className="select"
          options={options}
          styles={colourStyles}
          theme={(tm: any) => ({
            ...tm,
            borderRadius: 0,
            colors: {
              ...tm.colors,
              primary25: theme.palette.primary.main,
            },
          })}
          isMulti={isMulti}
          components={{ Option, DropdownIndicator: arrowDownIcon }}
          isDisabled={disabled}
          isSearchable={isSearchable}
          id={id}
          hideSelectedOptions={false}
          isClearable={false}
          value={inputValue}
          placeholder={''}
          closeMenuOnSelect={false}
          onChange={value => {
            handleChange(value);
          }}
          classNamePrefix="react-select"
        />
        <FormHelperText>{error && error.error ? error.message : customMessage()}</FormHelperText>
      </FormControl>
    </>
  );
};
export default MultiSelect;
